<template>
  <div
    style="height: 100%; width: 100%; display: flex; flex-direction: column"
    class="list"
  >
    <!-- Mobile step header shown during carrier search navigation -->
    <div v-if="isMobile && carrierSearchInput" class="mobile-nav-header">
      <v-btn icon size="small" variant="text" @click="handleMobileBack">
        <v-icon>mdi-arrow-left</v-icon>
      </v-btn>
      <span class="text-body-2 font-weight-medium ml-2">
        {{ showclickedCarrier ? carrierSelected : 'Search Results' }}
      </span>
    </div>

    <!-- Carrier View -->
    <div
      style="
        padding: 8px 12px;
        background-color: white;
        box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
        position: relative;
      "
    >
      <v-text-field
        v-model="carrierSearchInput"
        variant="outlined"
        flat
        hide-details
        clearable
        dense
        single-line
        label="Search carriers, patients, messages..."
        class="mt-0"
        bg-color="white"
        style="min-height: 40px"
        @focus="showSuggestions = true"
        @blur="hideSuggestionsDelayed"
        @keydown.esc="showSuggestions = false"
      >
        <template v-if="filter" v-slot:prepend-inner style="height: 60%">
          <v-badge
            :model-value="filterCount > 0"
            overlap
            :content="filterCount"
            bordered
            color="primary"
            style="margin-right: 8px"
          >
            <MiniSettings
              :sections="claimsSettings"
              @claimFilterChanged="handleFilterChange($event)"
              style="transform: scale(0.85)"
            />
          </v-badge>
        </template>
      </v-text-field>

      <!-- Suggestions dropdown -->
      <div
        v-if="showSuggestions && carrierSearchInput && hasSuggestions"
        class="suggestions-dropdown elevation-4"
      >
        <!-- Carrier matches -->
        <template v-if="carrierSuggestions.length">
          <div class="suggestion-header">Carriers</div>
          <div
            v-for="carrier in carrierSuggestions"
            :key="'c-' + carrier.name"
            class="suggestion-item"
            @mousedown.prevent="selectCarrier(carrier); showSuggestions = false"
          >
            <v-icon size="14" class="mr-1" color="blue-darken-2">mdi-office-building</v-icon>
            <span class="text-truncate" style="flex:1">{{ carrier.name }}</span>
            <span class="suggestion-count">{{ carrier.freq }}</span>
          </div>
        </template>

        <!-- Patient matches -->
        <template v-if="patientSuggestions.length">
          <div class="suggestion-header">Patients</div>
          <div
            v-for="patient in patientSuggestions"
            :key="'p-' + patient"
            class="suggestion-item"
            @mousedown.prevent="selectPatient(patient)"
          >
            <v-icon size="14" class="mr-1" color="green-darken-2">mdi-account</v-icon>
            <span class="text-truncate">{{ patient }}</span>
          </div>
        </template>

        <!-- Tag-matched conversations -->
        <template v-if="tagMatchedClaims.length">
          <div class="suggestion-header">Tagged Conversations</div>
          <div
            v-for="c in tagMatchedClaims"
            :key="'tc-' + c.DSSClaimsID"
            class="suggestion-item"
            @mousedown.prevent="selectTaggedClaim(c)"
          >
            <v-icon size="14" class="mr-1" color="purple-darken-2">mdi-tag</v-icon>
            <span class="text-truncate" style="flex:1">{{ c.convo?.convo_name || c.PatName }}</span>
            <span class="suggestion-count">{{ c.Carrier }}</span>
          </div>
        </template>

        <!-- Message search results -->
        <template v-if="messageSearchLoading || messageSearchResults.length">
          <div class="suggestion-header d-flex align-center">
            Messages
            <v-progress-circular
              v-if="messageSearchLoading"
              size="10"
              width="2"
              indeterminate
              class="ml-1"
            />
          </div>
          <div
            v-for="result in messageSearchResults.slice(0, 5)"
            :key="'m-' + result.DSSClaimsID"
            class="suggestion-item suggestion-item--message"
            @mousedown.prevent="selectMessageResult(result)"
          >
            <div class="text-caption font-weight-medium text-truncate" style="color:#333">
              {{ result.PatName }} — {{ result.Carrier }}
            </div>
            <div
              v-for="(snippet, si) in (result.snippets || []).slice(0, 2)"
              :key="si"
              class="text-caption text-truncate"
              style="color:#757575"
            >{{ snippet }}</div>
          </div>
        </template>
      </div>
    </div>

    <template v-if="carrierSearchInput">
      <v-divider class="my-0" />

      <div style="max-height: 60vh; display: flex; flex-direction: column">
        <div v-if="showAllclaims">
          <!-- Carrier Search Box -->
          <v-progress-linear
            v-if="loading"
            indeterminate
            color="primary"
            height="3"
          />

          <div
            v-if="!loading && !filteredCarriers.length"
            class="d-flex align-center justify-center"
            style="height: 100px; color: #9e9e9e"
          >
            <v-icon size="small" class="mr-2">mdi-magnify</v-icon>
            <span class="text-caption">No carriers match your search</span>
          </div>

          <div v-else style="display: flex; height: 85vh">
            <!-- Carrier List -->
            <v-icon v-if="carrierSearchInput" @click="backtoOSConvo()"
              >mdi-arrow-u-left-top</v-icon
            >
            <div
              style="
                flex: 1;
                display: flex;
                flex-direction: column;
                overflow: hidden;
                background-color: #fafafa;
                height: 100%;
              "
            >
              <RecycleScroller
                v-if="filteredCarriers.length > 0"
                :items="filteredCarriers"
                :item-size="60"
                key-field="name"
                v-slot="{ item }"
                style="height: 100%; width: 100%"
              >
                <v-hover v-slot="{ hover }">
                  <div
                    @click="selectCarrier(item)"
                    :class="{
                      'bg-blue-lighten-5': carrierSelected === item.name,
                      'bg-grey-lighten-4':
                        hover && carrierSelected !== item.name,
                    }"
                    style="
                      display: flex;
                      align-items: center;
                      height: 100%;
                      padding: 0 12px;
                      cursor: pointer;
                      border-bottom: 1px solid #f5f5f5;
                    "
                    class="unselectable"
                  >
                    <v-badge
                      overlap
                      bordered
                      :color="hashedColor(item.name)"
                      :content="item.urgent || ''"
                    >
                      <!-- freq will track how often each carrier appears.
                urgency will track how many claims from that carrier are considered urgent. -->
                      <v-avatar
                        :color="hashedColor(item.name)"
                        size="36"
                        class="elevation-1"
                      >
                        <span
                          class="white--text text-caption font-weight-medium"
                          >{{ item.freq }}</span
                        >
                      </v-avatar>
                    </v-badge>

                    <div style="flex: 1; margin: 0 12px; min-width: 0">
                      <div class="text-body-2 font-weight-medium text-truncate">
                        {{ item.name }}
                      </div>
                      <div
                        style="color: #757575"
                        class="text-caption text-truncate mt-n1"
                      >
                        {{ phoneFormat(item.num) }}
                      </div>
                    </div>

                    <v-btn
                      icon
                      variant="text"
                      size="small"
                      @click.stop="phoneCall(item.name, item.num)"
                      color="grey-darken-1"
                    >
                      <v-icon size="small" color="green"
                        >mdi-phone-classic</v-icon
                      >
                    </v-btn>
                  </div>
                </v-hover>
              </RecycleScroller>
            </div>
          </div>
        </div>
      </div>
      <!-- Claims Table -->
      <div
        v-if="showclickedCarrier"
        style="display: flex; flex-direction: column; height: 85vh"
      >
        <div
          class="px-4 py-2 d-flex align-center"
          style="background-color: white; border-bottom: 1px solid #e0e0e0"
        >
          <v-icon v-if="carrierSearchInput" @click="backtoAllclaims()"
            >mdi-arrow-u-left-top</v-icon
          >
          <div style="flex: 1; margin: 0 12px">
            <span class="text-subtitle-1 font-weight-medium">{{
              carrierSelected
            }}</span>
            <div
              style="color: #757575"
              class="text-caption text-truncate mt-n1"
            >
              {{ phoneFormat(carrierSelectedNum) }}
            </div>
          </div>
          <v-btn
            icon
            variant="text"
            size="small"
            @click.stop="phoneCall(carrierSelected, carrierSelectedNum)"
            color="grey-darken-1"
          >
            <v-icon size="small" color="green">mdi-phone-classic</v-icon>
          </v-btn>
          <span class="text-caption text-grey"
            >{{ carrierClaims.length }} claim{{
              carrierClaims.length !== 1 ? "s" : ""
            }}</span
          >
        </div>

        <div style="flex: 1; overflow: auto">
          <v-data-table-virtual
            :headers="claim_headers"
            :items="carrierClaims"
            item-key="DSSClaimsID"
            item-value="DSSClaimsID"
            v-model:expanded="expanded"
            show-expand
            @click:row="(event, props) => handleClaimClick(props.item)"
          >
            <!-- Status Icon -->
            <template v-slot:[`item.Status`]="{ item }">
              <v-tooltip location="left" :text="calcTooltip(item)">
                <template v-slot:activator="{ props }">
                  <v-icon
                    v-bind="props"
                    :color="getStatusColor(item)"
                    size="small"
                  >
                    {{ getStatusIcon(item) }}
                  </v-icon>
                </template>
              </v-tooltip>
            </template>

            <!-- Type -->
            <template v-slot:[`item.Type`]="{ item }">
              <span class="text-caption">{{ item.Type }}</span>
            </template>

            <!-- DOS Formatting -->
            <template v-slot:[`item.DOS`]="{ item }">
              <span class="text-caption">{{
                dayjs(item.DOS).format("MMM D, YYYY")
              }}</span>
            </template>

            <!-- Expand Button -->
            <template v-slot:item.data-table-expand="{ item }">
              <v-btn
                :icon="
                  expanded.includes(item.DSSClaimsID)
                    ? 'mdi-chevron-up'
                    : 'mdi-chevron-down'
                "
                size="small"
                variant="plain"
                @click.stop="toggleExpand(item)"
              />
            </template>

            <!-- Expanded Row: show extra details -->
            <template v-slot:expanded-row="{ item }">
              <tr>
                <td
                  :colspan="claim_headers.length + 1"
                  class="pa-3 bg-blue-lighten-5"
                >
                  <div class="d-flex">
                    <div
                      style="width: 120px"
                      class="font-weight-medium text-caption"
                    >
                      Date Sent:
                    </div>
                    <div class="text-caption">
                      {{ dayjs(item.DateSent).format("MMM D, YYYY h:mm A") }}
                    </div>
                  </div>
                  <div class="d-flex mt-1">
                    <div
                      style="width: 120px"
                      class="font-weight-medium text-caption"
                    >
                      Procedures:
                    </div>
                    <div class="text-caption">{{ item.Procedures }}</div>
                  </div>
                  <div class="d-flex mt-1">
                    <div
                      style="width: 120px"
                      class="font-weight-medium text-caption"
                    >
                      Amount:
                    </div>
                    <div class="text-caption">
                      {{ formatCurrency(item.Amount) }}
                    </div>
                  </div>
                </td>
              </tr>
            </template>
          </v-data-table-virtual>
        </div>
      </div>
    </template>

    <!-- Conversation View -->
    <template v-else>
      <div
        style="height: 100%; display: flex; flex-direction: column"
        class="list"
      >
        <div
          style="display: flex; justify-content: space-around; margin-top: 1rem"
        >
          <v-tooltip location="right" text="Claims Assistant">
            <template v-slot:activator="{ props }">
              <v-icon
                v-bind="props"
                style="flex: 1 1 1"
                @click="changeClaim({}); $emit('changePanel')"
                :color="!claim.DSSClaimsID ? 'primary' : ''"
              >
                mdi-robot-outline
              </v-icon>
            </template>
          </v-tooltip>
          <v-badge
            :model-value="nowClaims.some((c) => c.convo.unread)"
            dot
            overlap
          >
            <v-tooltip location="right" text="Now">
              <template v-slot:activator="{ props }">
                <v-icon
                  v-bind="props"
                  style="flex: 1 1 1"
                  @click="selectedTab = 'now'"
                  :color="selectedTab === 'now' ? 'primary' : ''"
                >
                  mdi-bell
                </v-icon>
              </template>
            </v-tooltip>
          </v-badge>
          <v-badge
            :model-value="laterClaims.some((c) => c.convo.unread)"
            dot
            overlap
          >
            <v-tooltip location="right" text="Later">
              <template v-slot:activator="{ props }">
                <v-icon
                  v-bind="props"
                  style="flex: 1 1 1"
                  @click="selectedTab = 'later'"
                  :color="selectedTab === 'later' ? 'primary' : ''"
                >
                 mdi-bell-plus
                </v-icon>
              </template>
            </v-tooltip>
          </v-badge>
        </div>
        <template>
        <v-divider style="margin: 0.5rem 0 0.5rem 0" />

        <div
          v-if="!loading && !displayedClaims.length"
          class="text-caption text-center"
          style="color: #757575"
        >
          No Conversations Here
        </div>
        <RecycleScroller
          v-else
          :items="displayedClaims"
          :item-size="35"
          key-field="DSSClaimsID"
          v-slot="{ item, index }"
          style="flex: 1 1 0; overflow-y: auto"
        >
          <v-hover v-slot="{ hover }">
            <div
              @click="change_Claim(item)"
              :class="{
                highlighted: claim.DSSClaimsID === item.DSSClaimsID,
                hovered: hover && claim.DSSClaimsID !== item.DSSClaimsID,
              }"
              :style="
                index === participatingIndex && selectedTab !== 'now'
                  ? 'border-top: 1px solid #777777;'
                  : ''
              "
              style="
                display: flex;
                justify-content: space-between;
                align-items: center;
                height: 35px;
              "
              class="unselectable"
            >
              <div
                style="
                  flex: 1 1 1;
                  white-space: nowrap;
                  overflow: hidden;
                  text-overflow: ellipsis;
                "
                class="mx-2 text-body-2"
                :class="item.convo.unread ? 'font-weight-bold' : ''"
              >
                <v-tooltip
                  v-if="
                    item.convo.userMentioned > 0 && !item.convo.userReminder
                  "
                  location="top"
                  text="You have an uncompleted task in this conversation with no reminder"
                >
                  <template v-slot:activator="{ props }">
                    <v-icon v-bind="props" color="primary">
                      mdi-rhombus-medium
                    </v-icon>
                  </template>
                </v-tooltip>
                {{ item.convo.convo_name }}
              </div>
              <div
                v-if="item.convo.userReminder"
                style="flex: 1 1 1; min-width: max-content"
                class="mr-1"
              >
                <v-tooltip
                  location="right"
                  :text="
                    dayjs(item.convo.userReminder.reminder_date).format(
                      'h:mmA M/D/YYYY'
                    )
                  "
                >
                  <template v-slot:activator="{ props }">
                    <div v-bind="props" style="font-size: 0.7rem">
                      <v-icon>mdi-alarm</v-icon>
                    </div>
                  </template>
                </v-tooltip>
              </div>
              <div v-else-if="selectedTab !== 'now' && !item.userParticipant">
                <v-tooltip
                  location="right"
                  text="You are not a participant of this conversation"
                >
                  <template v-slot:activator="{ props }">
                    <div v-bind="props">
                      <v-icon>mdi-account-off</v-icon>
                    </div>
                  </template>
                </v-tooltip>
              </div>
            </div>
          </v-hover>
        </RecycleScroller>
        </template>
      </div>
    </template>
     <!-- Conversation Name Dialog -->
    <v-dialog v-model="convoNameDialog" max-width="500px" persistent>
      <v-card>
        <v-toolbar color="primary" dark dense>
          <v-toolbar-title>Create Conversation</v-toolbar-title>
          <v-spacer></v-spacer>
          <v-btn icon @click="cancelConvoCreation">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-toolbar>
        <v-card-text class="pa-4">
          <v-text-field
            v-model="newConvoName"
            label="Conversation Name"
            outlined
            autofocus
            @keyup.enter="createNewConversation"
          ></v-text-field>
        </v-card-text>
        <v-card-actions class="pa-4">
          <v-spacer></v-spacer>
          <v-btn color="grey" text @click="cancelConvoCreation">Cancel</v-btn>
          <v-btn color="primary" @click="createNewConversation">Create</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import EventBus from "@/eventBus";
import colorMixin from "@/mixins/colorMixin";
import dayjsMixin from "@/mixins/dayjsMixin";
import MiniSettings from "@/components/MiniSettings";
import { mapGetters } from "vuex";
import { mapMutations, mapActions } from "vuex";
import { phoneFormat } from "@/util/util";
import ConvoService from "../../services/convoService";

export default {
  name: "LeftPanel",
  mixins: [colorMixin, dayjsMixin],
  components: { MiniSettings },
  props: {
    condition: Boolean,
  },
  data() {
    return {
      // For carrier view
      // In data()
      claim_headers: [
        { title: "Status", key: "Status" },
        { title: "Type", key: "Type" },
        { text: "Patient", value: "PatName" },
        { title: "DOS", key: "DOS" },
      ],
      showAllclaims: false,
      showclickedCarrier: false,
      expanded: [],
      // The carrier whose osclaims will be shown in the data table
      carrierSelected: null,
      carrierSelectedNum: null,
      carrierSearchInput: null,
      tableKey: 0,
      showSuggestions: false,
      searchDebounceTimer: null,

      // For conversation view
      selectedTab: "now",
      
      // For conversation dialog
      convoNameDialog: false,
      newConvoName: "",
      tempClaim: null,
    };
  },

  computed: {
    ...mapGetters("osclaims", [
      "claim",
      "filteredClaims",
      "convoID",
      "carriers",
      "lastSelectedCarrier",
      "claimsWithOpenConvos",
      "filter",
      "defaultFilter",
      "filterCount",
      "loading",
      "messageSearchResults",
      "messageSearchLoading",
    ]),
    ...mapGetters("window", ["isMobile"]),
    ...mapGetters(["userId"]),
    ...mapGetters("tags", ["osclaimsConvoTags"]),

    // Filtering carriers based on carrier search input
    filteredCarriers: function () {
      if (!this.carrierSearchInput) return [];

      // if user type all then show all the carriers
      if (this.carrierSearchInput?.toLowerCase() === "all") {
        console.log("the user find all carrier Search Input ");
        return this.carriers;
      }

      return this.carriers.filter((carrier) =>
        carrier.name
          .toLowerCase()
          .startsWith(this.carrierSearchInput.toLowerCase())
      );
    },
    // get all the osclaims of the carrier selected
  carrierClaims: function () {
  if (!this.carrierSelected) return [];

       // get the claims for the selected carrier
      // sort the claims by priority
      return [
        ...this.filteredClaims.filter(
          (c) => c.Carrier === this.carrierSelected
        ),
      ].sort((a, b) => {
        if (a.priority === b.priority && a.priority === 3) {
          if (!a.convo.userReminder) return -1;
          if (!b.convo.userReminder) return 1;
          return this.dayjs(a.convo.userReminder.reminder_date).isBefore(
            this.dayjs(b.convo.userReminder.reminder_date)
          )
            ? -1
            : 1;
        }
        return a.priority - b.priority ? -1 : 1;
      });
    },

    claimsSettings: function () {
      return [
        {
          type: "check-boxes",
          title: "Filter Claims by Type",
          eventName: "claimFilterChanged",
          options: this.defaultFilter.Type,
          defaultConfig: this.filter.Type,
        },
      ];
    },

    // Conversation view
    nowClaims() {
      return this.claimsWithOpenConvos.filter((c) => {
        if (
          c.convo.userReminder &&
          this.dayjs(c.convo.userReminder.reminder_date).isAfter(
            this.dayjs(),
            "day"
          )
        )
          return false;
        if (!c.convo.convo_participants.some((p) => p.user_id === this.userId))
          return false;
        return true;
      });
    },

    laterClaims() {
      return this.claimsWithOpenConvos
        .filter((c) => {
          // all conversations with reminders after today
          if (
            c.convo.userReminder &&
            this.dayjs(c.convo.userReminder.reminder_date).isAfter(
              this.dayjs(),
              "day"
            )
          )
            return true;
          // all conversations where the user is not a participant
          if (
            !c.convo.convo_participants.some((p) => p.user_id === this.userId)
          )
            return true;
          // otherwise
          return false;
        })
        .map((c) => {
          c.userParticipant = c.convo.convo_participants.some(
            (p) => p.user_id === this.userId
          );
          return c;
        });
    },
    // the claims to display in the current tab
    displayedClaims: function () {
      return this.selectedTab === "now" ? this.nowClaims : this.laterClaims;
    },
    participatingIndex: function () {
      return this.laterClaims.findIndex((c) => !c.userParticipant);
    },

    carrierSuggestions() {
      const q = (this.carrierSearchInput || '').toLowerCase();
      if (!q) return [];
      if (q === 'all') return this.carriers.slice(0, 6);
      return this.carriers
        .filter(c => c.name.toLowerCase().includes(q))
        .slice(0, 6);
    },

    patientSuggestions() {
      const q = (this.carrierSearchInput || '').toLowerCase();
      if (q.length < 2) return [];
      const names = new Set();
      this.filteredClaims.forEach(c => {
        if (c.PatName && c.PatName.toLowerCase().includes(q)) names.add(c.PatName);
      });
      return [...names].slice(0, 4);
    },

    tagMatchedClaims() {
      const q = (this.carrierSearchInput || '').toLowerCase();
      if (q.length < 1) return [];
      const matchedTagIds = new Set(
        this.osclaimsConvoTags
          .filter(t => t.tag_name.toLowerCase().includes(q))
          .map(t => t.tag_id)
      );
      if (!matchedTagIds.size) return [];
      return this.claimsWithOpenConvos
        .filter(c =>
          c.convo?.osclaims_conversation_tags?.some(oct => matchedTagIds.has(oct.tag_id))
        )
        .slice(0, 6);
    },

    hasSuggestions() {
      return (
        this.carrierSuggestions.length > 0 ||
        this.patientSuggestions.length > 0 ||
        this.tagMatchedClaims.length > 0 ||
        this.messageSearchResults.length > 0 ||
        this.messageSearchLoading
      );
    },
  },
  watch: {
    convoID() {
      this.carrierSelected = this.claim?.Carrier;
      const index = this.carriers.findIndex(
        (carrier) => carrier.name === this.carrierSelected
      );
      if (document.getElementById(index)) {
        document.getElementById(index).scrollIntoView();
      }
      this.expanded = this.claim ? [this.claim.DSSClaimsID] : [];
    },
    carrierSearchInput(newVal) {
      if (!newVal) {
        this.showAllclaims = false;
        this.showclickedCarrier = false;
        EventBus.$emit('leftPanelStepChanged', 'conversations');
        this.$store.commit('osclaims/setMessageSearchResults', []);
        return;
      }
      this.showAllclaims = true;
      this.showclickedCarrier = false;
      EventBus.$emit('leftPanelStepChanged', 'carrier-list');
      clearTimeout(this.searchDebounceTimer);
      const q = newVal.trim();
      if (q.length >= 2 && q.toLowerCase() !== 'all') {
        this.searchDebounceTimer = setTimeout(() => {
          this.$store.dispatch('osclaims/searchMessages', q);
        }, 350);
      } else {
        this.$store.commit('osclaims/setMessageSearchResults', []);
      }
    },
    // carrierClaims() {
    //   this.tableKey += 1;
    // },
  },
  methods: {
    ...mapMutations("osclaims", [
      "changeClaim",
      "changeLastSelectedCarrier",
      "changeFilter",
      "changeClaims",
    ]),
    ...mapActions("osclaims", ["retrieveClaims"]),
    hideSuggestionsDelayed() {
      setTimeout(() => { this.showSuggestions = false; }, 200);
    },
    selectTaggedClaim(claim) {
      this.changeClaim(claim);
      this.carrierSearchInput = null;
      this.showSuggestions = false;
      this.$emit('changePanel');
    },

    selectPatient(patientName) {
      const claim = this.filteredClaims.find(c => c.PatName === patientName);
      if (claim) {
        this.changeClaim(claim);
        this.carrierSearchInput = null;
        this.showSuggestions = false;
        this.$emit('changePanel');
      }
    },
    selectMessageResult(result) {
      const claim = this.filteredClaims.find(c => c.DSSClaimsID === result.DSSClaimsID);
      if (claim) {
        this.changeClaim(claim);
        this.carrierSearchInput = null;
        this.showSuggestions = false;
        this.$emit('changePanel');
      }
    },
    // Carrier view
    handleFilterChange(event) {
      this.changeFilter({ Type: event });
    },
    backtoAllclaims() {
      this.showclickedCarrier = false;
      this.showAllclaims = true;
      EventBus.$emit('leftPanelStepChanged', 'carrier-list');
    },
    backtoOSConvo() {
      this.carrierSearchInput = null;
      this.showclickedCarrier = false;
      this.showAllclaims = false;
      EventBus.$emit('leftPanelStepChanged', 'conversations');
    },
    selectCarrier(item) {
      this.carrierSelectedNum = item.num;
      this.carrierSelected = item.name;
      this.showclickedCarrier = true;
      this.showAllclaims = false;
      EventBus.$emit('leftPanelStepChanged', 'carrier-claims');
    },

    calcTooltip(item) {
      if (item.priority === 1)
        return "This claim is closed in this application but not in Open Dental";
      if (item.priority === 2) return "unopened claim";
      if (item.priority === 3 && item.convo.userReminder) {
        return this.dayjs(item.convo.userReminder.reminder_date).format(
          "h:mmA M/D/YYYY"
        );
      }
      if (item.priority === 3) return "no reminder";
      return "closed";
    },

    rowStyle(item) {
      let style = "";
      if (item.priority === 2) style += "font-weight-bold ";
      if (item.DSSClaimsID === this.claim?.DSSClaimsID) style += "highlighted";
      return style;
    },

    phoneCall(name, number) {
      console.log("this call num and name ", name, number);
      this.carrierSelected = name;
      EventBus.$emit("call-event", { name, number });
    },
    toggleExpand(item) {
      console.log("the Data your are Expand", item);
      const id = item.DSSClaimsID;
      const index = this.expanded.findIndex((exp) => exp === id);
      if (index > -1) {
        this.expanded.splice(index, 1);
      } else {
        this.expanded.push(id);
      }
    },
    change_Claim(item){
      this.changeClaim(item);
      this.$emit('changePanel');
    },
       async handleClaimClick(item) {
      if (!item) return;

      const claim = item;
      if (!claim.DSSClaimsID && claim.PatName) return;

      const existingConvoId = claim.convo_id || claim.convo?.convo_id;
      if (existingConvoId) {
        this.changeClaim(claim);
        this.expanded = [claim];
        this.$store.commit('osclaims/setConvoID', existingConvoId);
      } else {
        this.tempClaim = claim;
        this.newConvoName = claim.PatName || 'New Claim Conversation';
        this.convoNameDialog = true;
      }
    },
    
    async createNewConversation() {
  if (!this.tempClaim || !this.newConvoName) return;

  try {
    const claim = this.tempClaim;

    const response = await ConvoService.addConvo({
      system_process: "outstanding claim",
      system_process_id: claim.DSSClaimsID,
      convo_name: this.newConvoName,
      message: `Conversation "${this.newConvoName}" started`,
      secured: 0
    });

    const newConvo = response.data.convo || response.data;
    const updatedClaim = {
      ...claim,
      priority: claim.priority || this.calculatePriority(claim),
      convo: {
        ...newConvo,
        convo_mentions: newConvo.convo_mentions || [],
        convo_participants: newConvo.convo_participants || [],
        convo_reminders: newConvo.convo_reminders || [],
        convo_status: newConvo.convo_status ?? true,
        messages_updated_at: newConvo.messages_updated_at,
        date_created: newConvo.date_created,
        owner: newConvo.owner,
        userMentioned: 0,
        userReminder: newConvo.convo_reminders?.[0] || null,
        client_id: newConvo.client_id,
        system_process: newConvo.system_process,
        secured: newConvo.secured ?? false
      },
      convo_id: newConvo.convo_id ?? newConvo.id ?? null
    };

    this.changeClaim(updatedClaim);
    this.$store.commit('osclaims/setConvoID', updatedClaim.convo_id);
    this.expanded = [updatedClaim];
    this.$emit('changePanel', updatedClaim);
    this.retrieveClaims("osclaims");

    this.convoNameDialog = false;
    this.tempClaim = null;
    this.newConvoName = "";

  } catch (error) {
    console.error("Error creating conversation:", error);
    EventBus.$emit("alert", "Failed to create conversation");
    this.convoNameDialog = false;
  }
},
      calculatePriority(claim) {
    // Implement your actual priority logic here
    if (claim.convo?.userReminder) return 3;
    if (claim.convo) return 3;
    return 2; // Default priority
  },
    cancelConvoCreation() {
      this.convoNameDialog = false;
      this.tempClaim = null;
      this.newConvoName = "";
    },
    handleMobileBack() {
      if (this.showclickedCarrier) {
        this.backtoAllclaims();
      } else {
        this.backtoOSConvo();
      }
    },
    getStatusColor(item) {
      if (item.priority === 1) return "red";
      if (item.priority === 2) return "primary";
      if (item.priority === 3 && item.convo.userReminder) return "orange";
      return "grey";
    },

    getStatusIcon(item) {
      if (item.priority === 1) return "mdi-exclamation-thick";
      if (item.priority === 2) return "mdi-alert-decagram";
      if (item.priority === 3 && item.convo.userReminder)
        return "mdi-clock-alert-outline";
      if (item.priority === 3) return "mdi-alarm-off";
      return "mdi-check";
    },

    formatCurrency(amount) {
      return "$" + parseFloat(amount).toFixed(2);
    },

    phoneFormat,
  },
  mounted() {
    this.changeClaims([]);
    this.retrieveClaims("osclaims");

    this.carrierSelected = this.lastSelectedCarrier;
    this.expanded = this.claim ? [this.claim.DSSClaimsID] : [];

    EventBus.$on('leftPanelBack', (target) => {
      if (target === 'carrier-list') {
        this.backtoAllclaims();
      } else if (target === 'conversations') {
        this.backtoOSConvo();
      }
    });
  },
  beforeUnmount() {
    this.changeLastSelectedCarrier(this.carrierSelected);
    EventBus.$off('leftPanelBack');
  },
};
</script>

<style scoped>
.highlighted {
  background-color: #e3f2fd !important;
}

.hovered {
  background-color: #f5f5f5;
}

.v-data-table-virtual :deep(.v-data-table__td) {
  font-size: 0.8125rem !important;
  padding: 8px 12px !important;
  height: 42px !important;
}

.v-data-table-virtual :deep(.v-data-table__th) {
  font-size: 0.75rem !important;
  font-weight: 600 !important;
  letter-spacing: 0.5px;
  color: #616161 !important;
  padding: 8px 12px !important;
  height: 36px !important;
  background-color: #f5f5f5 !important;
}

.text-truncate {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.v-list-item {
  min-height: 48px !important;
}

.v-data-table-virtual :deep(tr:hover td) {
  background-color: #f5f9ff !important;
  cursor: pointer;
}

.suggestions-dropdown {
  position: absolute;
  top: calc(100% - 4px);
  left: 12px;
  right: 12px;
  background: white;
  border-radius: 6px;
  border: 1px solid #e0e0e0;
  z-index: 200;
  max-height: 320px;
  overflow-y: auto;
}

.suggestion-header {
  font-size: 0.65rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.6px;
  color: #9e9e9e;
  padding: 6px 12px 2px;
}

.suggestion-item {
  display: flex;
  align-items: center;
  padding: 6px 12px;
  cursor: pointer;
  font-size: 0.8125rem;
  transition: background 0.12s;
}

.suggestion-item:hover {
  background-color: #f0f4ff;
}

.suggestion-item--message {
  flex-direction: column;
  align-items: flex-start;
  padding: 5px 12px;
}

.suggestion-count {
  font-size: 0.7rem;
  color: #9e9e9e;
  margin-left: auto;
  padding-left: 6px;
}

.mobile-nav-header {
  display: flex;
  align-items: center;
  padding: 4px 8px;
  min-height: 44px;
  background-color: white;
  border-bottom: 1px solid #e0e0e0;
  flex-shrink: 0;
}
</style>
